import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  MessageCircle,
  Moon,
  Sun,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import logo from "../assets/images/ivin-logo.png";
import logoDark from "../assets/images/ivin-logo-dark.png";
import withLoader from "../redux/Hoc/withLoader";
import Api from "../redux/api";
import { chatRef } from "../screens/ChatPromptScreen";

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  setLoader: (loader: boolean) => void;
  loader: boolean;
}

interface Category {
  name: string;
  questions: string[];
}

interface ApiResponse {
  data: {
    category: Category[];
  };
  message: string;
  status: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  onToggle,
  setLoader,
  loader,
}) => {
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [categories, setCategories] = useState<Category[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set()
  );
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        if (window.innerWidth < 768 && isOpen) {
          onToggle();
        }
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onToggle]);

  // Search logic
  const searchCategories = useCallback(
    (query: string, categories: Category[]) => {
      if (!query.trim()) {
        return categories;
      }

      const searchTerm = query.toLowerCase().trim();

      return categories
        .map((category) => {
          const matchingQuestions = category.questions.filter((question) =>
            question.toLowerCase().includes(searchTerm)
          );

          // Include category if it has matching questions or if category name matches
          if (
            matchingQuestions.length > 0 ||
            category.name.toLowerCase().includes(searchTerm)
          ) {
            return {
              ...category,
              questions: category.name.toLowerCase().includes(searchTerm)
                ? category.questions
                : matchingQuestions,
            };
          }

          return null;
        })
        .filter((category): category is Category => category !== null);
    },
    []
  );

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Update filtered categories when debounced search query or categories change
  useEffect(() => {
    const filtered = searchCategories(debouncedSearchQuery, categories);
    setFilteredCategories(filtered);

    // Auto-expand categories that have search results
    if (debouncedSearchQuery.trim()) {
      const expandedSet = new Set<string>();
      filtered.forEach((category) => {
        if (
          category.questions.some((question) =>
            question.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
          )
        ) {
          expandedSet.add(category.name);
        }
      });
      setExpandedCategories(expandedSet);
    } else {
      // Collapse all categories when search is cleared
      setExpandedCategories(new Set());
    }
  }, [debouncedSearchQuery, categories, searchCategories]);

  const toggleCategory = (categoryName: string) => {
    setExpandedCategories((prev) => {
      // If the category is already open, close it
      if (prev.has(categoryName)) {
        return new Set();
      } else {
        // If opening a category, close all others and open only this one
        return new Set([categoryName]);
      }
    });
  };

  // Highlight search terms in text
  const highlightText = (text: string, searchTerm: string) => {
    if (!searchTerm.trim()) return text;

    const regex = new RegExp(
      `(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi"
    );
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark key={index} className="bg-tertiary/20 text-tertiary px-1 rounded">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  const fetchFaq = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await Api.get(`/ai/app-settings`, {
        params: {
          id: "web_prompt",
        },
      });
      const response: ApiResponse = res.data;
      if (response.status && response.data.category) {
        setCategories(response.data.category);
        setFilteredCategories(response.data.category);
        // Collapse all categories when refreshing
        setExpandedCategories(new Set());
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading]);

  const handleQuestionClick = useCallback(
    (question: string) => {
      if (loader) return; // Disable when loader is on
      chatRef.current?.handleSendQuestion(question);
    },
    [loader]
  );

  useEffect(() => {
    fetchFaq();
  }, [fetchFaq]);

  return (
    <>
      {/* Mobile Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 0.2,
              ease: "easeInOut",
            }}
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={onToggle}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.div
        ref={sidebarRef}
        initial={false}
        animate={{
          x: isOpen ? 0 : -320, // -320px = -w-80
        }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 30,
        }}
        className="fixed left-0 top-0 h-full w-80 md:w-96 md:relative md:translate-x-0 z-50 p-2"
      >
        <div className="flex flex-col h-full bg-white dark:bg-primary border border-gray-200 dark:border-secondary shadow-lg rounded-3xl">
          {/* Header */}
          <div className="flex gap-6 flex-col p-6 border-b border-gray-200 dark:border-secondary">
            <div className="flex items-center justify-between">
              <a
                href="https://vinsyt.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 relative"
              >
                <motion.img
                  className="h-10 hidden dark:block"
                  src={logo}
                  alt="ivin logo"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                />
                <motion.img
                  className="h-10 dark:hidden"
                  src={logoDark}
                  alt="ivin logo"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                />
              </a>
              <div className="flex items-center gap-2">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={toggleTheme}
                  className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-secondary rounded-lg transition-colors"
                >
                  {theme === "dark" ? <Sun size={20} /> : <Moon size={20} />}
                </motion.button>
                <button
                  onClick={onToggle}
                  className="md:hidden text-gray-500 dark:text-gray-400 p-2 hover:bg-gray-100 dark:hover:bg-secondary rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Search Input */}
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="flex items-center gap-3"
              >
                <input
                  type="text"
                  placeholder="Search Prompts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  disabled={loader}
                  className={`w-full px-3 py-2 border border-gray-300 dark:border-secondary rounded-lg focus:outline-none focus:ring-2 focus:ring-tertiary focus:border-transparent bg-white dark:bg-primary text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                    loader ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                />
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Categories List */}
          <div
            className={`flex-1 overflow-y-auto p-4 ${
              loader ? "pointer-events-none" : ""
            }`}
          >
            {/* Search Results Info */}
            <AnimatePresence>
              {searchQuery.trim() && (
                <motion.div
                  initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                  animate={{ opacity: 1, height: "auto", marginBottom: 16 }}
                  exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                  transition={{ duration: 0.2, ease: "easeInOut" }}
                  className="px-3 py-2 bg-gray-50 dark:bg-secondary rounded-lg"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {searchQuery !== debouncedSearchQuery ? (
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-tertiary border-t-transparent rounded-full animate-spin"></div>
                          Searching...
                        </span>
                      ) : filteredCategories.length > 0 ? (
                        `Found ${filteredCategories.reduce(
                          (total, cat) => total + cat.questions.length,
                          0
                        )} prompts in ${filteredCategories.length} categories`
                      ) : (
                        "No matching prompts found"
                      )}
                    </p>
                    {searchQuery.trim() && (
                      <button
                        onClick={() => setSearchQuery("")}
                        className="text-tertiary hover:text-tertiary/80 text-xs font-medium"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Loading Indicator */}
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-tertiary"></div>
                  <span className="text-sm">Processing...</span>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredCategories.length > 0 ? (
                  filteredCategories.map((category, categoryIndex) => (
                    <motion.div
                      key={category.name}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: categoryIndex * 0.1 }}
                      className="border border-gray-200 dark:border-secondary rounded-lg overflow-hidden"
                    >
                      {/* Category Header */}
                      <button
                        onClick={() => toggleCategory(category.name)}
                        disabled={loader}
                        className={`w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-secondary hover:bg-gray-100 dark:hover:bg-secondary/80 transition-colors ${
                          loader ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      >
                        <div className="w-full flex items-center gap-3">
                          <MessageCircle size={16} className="text-tertiary" />
                          <span
                            className="flex-1 text-start font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap overflow-hidden text-ellipsis"
                            title={category.name}
                          >
                            {category.name}
                          </span>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded-full">
                              {category.questions.length}
                            </span>
                            {expandedCategories.has(category.name) ? (
                              <ChevronDown
                                size={16}
                                className="text-gray-500 dark:text-gray-400"
                              />
                            ) : (
                              <ChevronRight
                                size={16}
                                className="text-gray-500 dark:text-gray-400"
                              />
                            )}
                          </div>
                        </div>
                      </button>

                      {/* Questions List */}
                      <AnimatePresence>
                        {expandedCategories.has(category.name) && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <div className="border-t border-gray-200 dark:border-secondary">
                              {category.questions.map(
                                (question, questionIndex) => (
                                  <motion.button
                                    key={questionIndex}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: questionIndex * 0.05 }}
                                    onClick={() =>
                                      handleQuestionClick(question)
                                    }
                                    disabled={loader}
                                    className={`w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-primary transition-colors border-b border-gray-100 dark:border-secondary last:border-b-0 ${
                                      loader
                                        ? "opacity-50 cursor-not-allowed"
                                        : "hover:bg-gray-50 dark:hover:bg-primary"
                                    }`}
                                  >
                                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                                      {highlightText(
                                        question,
                                        debouncedSearchQuery
                                      )}
                                    </p>
                                  </motion.button>
                                )
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <MessageCircle
                      size={48}
                      className="text-gray-300 dark:text-gray-600 mb-4"
                    />
                    <p className="text-gray-500 dark:text-gray-400 text-sm">
                      {searchQuery.trim()
                        ? "No prompts found matching your search"
                        : "No prompts available"}
                    </p>
                    {searchQuery.trim() && (
                      <button
                        onClick={() => setSearchQuery("")}
                        className="mt-2 text-tertiary hover:text-tertiary/80 text-sm font-medium"
                      >
                        Clear search
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200 dark:border-secondary space-y-2">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-3 w-full px-3 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-secondary rounded-lg transition-all duration-200"
              onClick={() =>
                window.open(
                  "https://vinsyt.com/demo?utm_source=ivin-web&utm_medium=button&utm_campaign=demo_request&utm_content=footer",
                  "_blank"
                )
              }
            >
              <MessageCircle size={20} />
              <span className="font-medium">Contact Us</span>
            </motion.button>
          </div>
        </div>
      </motion.div>
    </>
  );
};

const SidebarWithLoader = withLoader(Sidebar);
export default SidebarWithLoader;
